import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { 
  Plus, 
  MoreHorizontal, 
  Edit, 
  Trash2,
  UserCog,
  Users,
  Mail,
  Lock,
  User
} from "lucide-react";

const UsersPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null, userName: "" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "sales_rep"
  });
  const token = localStorage.getItem("token");
  // The backend refuses edits to the super-admin account from anyone else,
  // and role changes to it or to yourself; the UI mirrors that.
  const viewerIsSuperAdmin = !!user?.is_super_admin;
  const roleLocked = !!editingUser && (editingUser.user_id === user.user_id || editingUser.is_super_admin);
  const canEditUser = (u) => !u.is_super_admin || viewerIsSuperAdmin;
  const canDeleteUser = (u) => u.user_id !== user.user_id && !u.is_super_admin;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "sales_rep"
    });
    setIsModalOpen(true);
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role
    });
    setIsModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorDetail(error, "Failed to delete user"));
    }
  };

  const confirmDeleteUser = (userId, userName) => {
    if (userId === user.user_id) {
      toast.error("You cannot delete yourself");
      return;
    }
    setDeleteConfirm({ open: true, userId, userName });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.user_id}`, {
          name: formData.name,
          role: formData.role
        }, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("User updated");
      } else {
        await axios.post(`${API}/users`, formData, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("User created");
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorDetail(error, "Failed to save user"));
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="users-page">
      <Sidebar user={user} onLogout={handleLogout} />
      
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-fade-in">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Team Management
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your sales team members
            </p>
          </div>
          <Button
            onClick={handleCreateUser}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-slate-100 shadow-sm animate-fade-in stagger-1">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-indigo-100">
                  <Users className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100 shadow-sm animate-fade-in stagger-2">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-emerald-100">
                  <User className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Sales Reps</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {users.filter(u => u.role === "sales_rep").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100 shadow-sm animate-fade-in stagger-3">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-amber-100">
                  <UserCog className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Managers</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {users.filter(u => u.role === "manager").length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="border-slate-100 shadow-sm animate-fade-in stagger-4">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="spinner mx-auto"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <p>No team members yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">User</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow 
                      key={u.user_id} 
                      className="table-row-hover"
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-medium">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            {u.user_id === user.user_id && (
                              <span className="text-xs text-slate-500">(You)</span>
                            )}
                            {u.is_super_admin && (
                              <span className="text-xs text-amber-600 ml-1" data-testid={`super-admin-label-${u.user_id}`}>
                                Super admin
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={
                          u.role === "manager" 
                            ? "bg-amber-100 text-amber-700" 
                            : "bg-indigo-100 text-indigo-700"
                        }>
                          {u.role === "manager" ? "Manager" : "Sales Rep"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {(canEditUser(u) || canDeleteUser(u)) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`user-actions-${u.user_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEditUser(u) && (
                                <DropdownMenuItem onClick={() => handleEditUser(u)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              {canDeleteUser(u) && (
                                <DropdownMenuItem 
                                  onClick={() => confirmDeleteUser(u.user_id, u.name)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingUser ? "Edit User" : "Add New User"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10"
                  placeholder="John Doe"
                  required
                  data-testid="user-name-input"
                />
              </div>
            </div>
            
            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      placeholder="john@company.com"
                      required
                      data-testid="user-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10"
                      placeholder="••••••••"
                      minLength={6}
                      required
                      data-testid="user-password-input"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                disabled={roleLocked}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales_rep">Sales Rep</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
              {roleLocked && (
                <p className="text-xs text-slate-500">
                  {editingUser.is_super_admin
                    ? "The super-admin account always stays a manager."
                    : "You can't change your own role."}
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="save-user-btn"
              >
                {editingUser ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, userId: null, userName: "" })}
        title={`Delete ${deleteConfirm.userName}?`}
        description="Are you sure you want to delete this user? Their leads will be reassigned to you. This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={() => handleDeleteUser(deleteConfirm.userId)}
      />
    </div>
  );
};

export default UsersPage;
